import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const QUALIFYING_VARIANT_IDS = new Set([
  46604717883586,
  46604717916354,
  46604722864322,
  46604722897090,
  46604722929858,
  46604722962626,
]);

const GIFT_VARIANT_ID = 48480138952898;
const REQUIRED_PUMP_QUANTITY = 2;
const GIFT_PROPERTY_NAME = '_Regalo EMYASA';
const GIFT_PROPERTY_VALUE = 'Kit de Válvulas gratis';
const EVENT_SOURCE = 'emyasa-free-gift';
const FALLBACK_SECTION_ID = 'cart-drawer-section';

const nativeFetch = window.fetch.bind(window);

let isSynchronizing = false;
let synchronizationQueued = false;
let synchronizationTimer = null;
let queuedCartSnapshot = null;

function getShopifyRoot() {
  return window.Shopify?.routes?.root || '/';
}

function buildCartUrl(path) {
  return `${getShopifyRoot()}${path.replace(/^\//, '')}`;
}

function getCartSectionIds() {
  const sectionIds = Array.from(
    document.querySelectorAll('cart-items-component[data-section-id]')
  )
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);

  return [...new Set(sectionIds.length ? sectionIds : [FALLBACK_SECTION_ID])];
}

function getSectionRequestData() {
  return {
    sections: getCartSectionIds().join(','),
    sections_url: window.location.pathname,
  };
}

function isPromotionalGiftLine(item) {
  if (Number(item.variant_id) !== GIFT_VARIANT_ID) return false;

  const properties = item.properties || {};
  return properties[GIFT_PROPERTY_NAME] === GIFT_PROPERTY_VALUE;
}

function getQualifyingPumpQuantity(cart) {
  return (cart.items || []).reduce((total, item) => {
    if (!QUALIFYING_VARIANT_IDS.has(Number(item.variant_id))) return total;
    return total + Number(item.quantity || 0);
  }, 0);
}

function getCartItemCount(cart) {
  if (Number.isFinite(Number(cart.item_count))) {
    return Number(cart.item_count);
  }

  return (cart.items || []).reduce(
    (total, item) => total + Number(item.quantity || 0),
    0
  );
}

async function requestJson(url, options = {}) {
  const headers = new Headers(options.headers || {});
  headers.set('Accept', 'application/json');

  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await nativeFetch(url, {
    ...options,
    credentials: 'same-origin',
    headers,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = data.description || data.message || `Error ${response.status}`;
    throw new Error(message);
  }

  return data;
}

function fetchCart() {
  return requestJson(buildCartUrl('cart.js'), {
    method: 'GET',
    cache: 'no-store',
  });
}

function addPromotionalGift() {
  return requestJson(buildCartUrl('cart/add.js'), {
    method: 'POST',
    body: JSON.stringify({
      items: [
        {
          id: GIFT_VARIANT_ID,
          quantity: 1,
          properties: {
            [GIFT_PROPERTY_NAME]: GIFT_PROPERTY_VALUE,
          },
        },
      ],
      ...getSectionRequestData(),
    }),
  });
}

function updatePromotionalGiftLines(updates) {
  return requestJson(buildCartUrl('cart/update.js'), {
    method: 'POST',
    body: JSON.stringify({
      updates,
      ...getSectionRequestData(),
    }),
  });
}

function createCartSnapshotFromEvent(result) {
  const items = result?.detail?.items;

  if (!Array.isArray(items)) return null;

  return {
    items,
    item_count: items.reduce(
      (total, item) => total + Number(item.quantity || 0),
      0
    ),
    sections: result.detail?.sections || {},
  };
}

function mergeGiftAddResponse(cart, response) {
  const addedItems = Array.isArray(response.items)
    ? response.items
    : response.variant_id
      ? [response]
      : [];

  return {
    ...cart,
    items: [...(cart.items || []), ...addedItems],
    item_count:
      getCartItemCount(cart) +
      addedItems.reduce(
        (total, item) => total + Number(item.quantity || 0),
        0
      ),
    sections: response.sections || {},
  };
}

function dispatchCartRefresh(cart) {
  const deferredEvent = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: [],
      promise: deferredEvent.promise,
    })
  );

  deferredEvent.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items || [],
      source: EVENT_SOURCE,
      sourceId: EVENT_SOURCE,
      itemCount: getCartItemCount(cart),
      sections: cart.sections || {},
      didError: false,
    },
  });
}

async function synchronizePromotionalGift(cartSnapshot = null) {
  if (isSynchronizing) {
    synchronizationQueued = true;

    if (cartSnapshot?.items) {
      queuedCartSnapshot = cartSnapshot;
    }

    return;
  }

  isSynchronizing = true;

  try {
    const cart = cartSnapshot?.items ? cartSnapshot : await fetchCart();
    const promotionalGiftLines = (cart.items || []).filter(isPromotionalGiftLine);
    const customerIsEligible =
      getQualifyingPumpQuantity(cart) >= REQUIRED_PUMP_QUANTITY;

    if (customerIsEligible && promotionalGiftLines.length === 0) {
      const addResponse = await addPromotionalGift();
      const updatedCart = mergeGiftAddResponse(cart, addResponse);
      dispatchCartRefresh(updatedCart);
      return;
    }

    if (customerIsEligible && promotionalGiftLines.length > 0) {
      const updates = {};

      promotionalGiftLines.forEach((line, index) => {
        const desiredQuantity = index === 0 ? 1 : 0;

        if (Number(line.quantity) !== desiredQuantity) {
          updates[line.key] = desiredQuantity;
        }
      });

      if (Object.keys(updates).length > 0) {
        const updatedCart = await updatePromotionalGiftLines(updates);
        dispatchCartRefresh(updatedCart);
      }

      return;
    }

    if (!customerIsEligible && promotionalGiftLines.length > 0) {
      const updates = {};

      promotionalGiftLines.forEach((line) => {
        updates[line.key] = 0;
      });

      const updatedCart = await updatePromotionalGiftLines(updates);
      dispatchCartRefresh(updatedCart);
    }
  } catch (error) {
    console.warn('[EMYASA] No fue posible sincronizar el kit promocional.', error);
  } finally {
    isSynchronizing = false;

    if (synchronizationQueued) {
      synchronizationQueued = false;
      const nextSnapshot = queuedCartSnapshot;
      queuedCartSnapshot = null;
      queueSynchronization(0, nextSnapshot);
    }
  }
}

function queueSynchronization(delay = 40, cartSnapshot = null) {
  if (cartSnapshot?.items) {
    queuedCartSnapshot = cartSnapshot;
  }

  window.clearTimeout(synchronizationTimer);
  synchronizationTimer = window.setTimeout(() => {
    const nextSnapshot = queuedCartSnapshot;
    queuedCartSnapshot = null;
    synchronizePromotionalGift(nextSnapshot);
  }, delay);
}

function handleCartLinesUpdate(event) {
  const eventPromise = event?.promise;

  if (!eventPromise || typeof eventPromise.then !== 'function') {
    queueSynchronization();
    return;
  }

  eventPromise
    .then((result) => {
      const source = result?.detail?.source;
      const sourceId = result?.detail?.sourceId;

      if (source === EVENT_SOURCE || sourceId === EVENT_SOURCE) return;
      if (result?.detail?.didError) return;

      queueSynchronization(0, createCartSnapshotFromEvent(result));
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[EMYASA] No fue posible leer el cambio del carrito.', error);
      }
    });
}

function isObservedCartMutation(input, init) {
  const request = input instanceof Request ? input : null;
  const rawUrl =
    request?.url ||
    (typeof input === 'string' || input instanceof URL ? input.toString() : '');

  if (!rawUrl) return false;

  const method = String(init?.method || request?.method || 'GET').toUpperCase();

  if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) return false;

  const pathname = new URL(rawUrl, window.location.origin).pathname;

  return /\/cart\/(add|change|update|clear)\.js$/.test(pathname);
}

function installCartMutationBridge() {
  if (window.__emyasaFreeGiftFetchBridgeInstalled) return;

  const storefrontFetch = window.fetch.bind(window);

  window.fetch = async (...args) => {
    const shouldObserve = isObservedCartMutation(args[0], args[1]);
    const response = await storefrontFetch(...args);

    if (shouldObserve && response.ok) {
      queueSynchronization(30);
    }

    return response;
  };

  window.__emyasaFreeGiftFetchBridgeInstalled = true;
}

function initializePromotionalGift() {
  installCartMutationBridge();
  document.addEventListener(StandardEvents.cartLinesUpdate, handleCartLinesUpdate);
  document.addEventListener('shopify:section:load', () =>
    queueSynchronization(0)
  );
  window.addEventListener('pageshow', () => queueSynchronization(0));
  queueSynchronization(0);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializePromotionalGift, {
    once: true,
  });
} else {
  initializePromotionalGift();
}
